import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, extractErrorMessage } from "../lib/api";
import { useAuth, hasAccess } from "../context/AuthContext";
import { ChevronLeft, Plus, Trash2, X, Cpu, Monitor, History, Search, ArrowRight } from "lucide-react";

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}

const LEVITO_PLACEHOLDER = "To be filled by Levito";

export default function ControllersApp({
  apiPath = "/controllers",
  appId = "controllers",
  title = "Controllers",
  subtitle = "Registry of every chamber in the field — firmware, customer, update history.",
  productBadge = null,
} = {}) {
  const { user } = useAuth();
  const canWrite = hasAccess(user, appId, true);

  const [controllers, setControllers] = useState([]);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const [showControllerModal, setShowControllerModal] = useState(false);
  const [controllerDraft, setControllerDraft] = useState(blankController());

  const [historyFor, setHistoryFor] = useState(null);

  const fetchAll = async () => {
    try {
      const { data } = await api.get(apiPath);
      setControllers(data);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  useEffect(() => {
    fetchAll();
  }, [apiPath]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return controllers;
    return controllers.filter((c) => {
      const hay = [
        String(c.serial_number),
        c.customer || "",
        c.chamber_model || "",
        c.location || "",
        c.current_version || "",
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [controllers, query]);

  const submitController = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        serial_number: Number(controllerDraft.serial_number),
        chamber_model: controllerDraft.chamber_model,
        customer: controllerDraft.customer?.trim() || null,
        location: controllerDraft.location?.trim() || null,
        current_version: controllerDraft.current_version?.trim() || null,
        last_update: controllerDraft.last_update ? new Date(controllerDraft.last_update).toISOString() : null,
        notes: controllerDraft.notes?.trim() || null,
      };
      if (controllerDraft.id) {
        await api.patch(`${apiPath}/${controllerDraft.id}`, payload);
      } else {
        await api.post(apiPath, payload);
      }
      setShowControllerModal(false);
      setControllerDraft(blankController());
      fetchAll();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const deleteController = async (id) => {
    if (!window.confirm("Remove this controller from the registry?")) return;
    try {
      await api.delete(`${apiPath}/${id}`);
      fetchAll();
    } catch (err) { setError(extractErrorMessage(err)); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
        <Link to="/" data-testid="controllers-back-link" className="inline-flex items-center gap-1 text-envicom-muted hover:text-envicom-text font-mono text-sm">
          <ChevronLeft className="w-4 h-4" /> Back to tools
        </Link>

        <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded flex items-center justify-center bg-envicom-purple/15 text-envicom-purple border border-envicom-purple/30">
              <Monitor className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-mono text-2xl md:text-3xl font-bold tracking-tight">{title}</h1>
                {productBadge && (
                  <span
                    data-testid="controllers-product-badge"
                    className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded border ${productBadge.className}`}
                  >
                    {productBadge.label}
                  </span>
                )}
              </div>
              <p className="text-envicom-muted text-sm">{subtitle}</p>
            </div>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={() => { setControllerDraft(blankController()); setShowControllerModal(true); }}
              data-testid="controllers-new-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded bg-envicom-accent hover:bg-sky-400 text-black font-semibold font-mono text-sm"
            >
              <Plus className="w-4 h-4" /> Add controller
            </button>
          )}
        </div>

        {error && (
          <div data-testid="controllers-error" className="mt-4 px-3 py-2 rounded bg-envicom-error/10 border border-envicom-error/30 text-envicom-error text-sm">
            {error}
          </div>
        )}

        <div className="mt-6 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-envicom-muted" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search serial, customer, model, location…"
            data-testid="controllers-search-input"
            className="w-full bg-envicom-input border border-envicom-border rounded pl-9 pr-3 py-2.5 text-envicom-text text-sm focus:outline-none focus:border-envicom-accent font-mono"
          />
        </div>

        <section className="mt-6 bg-envicom-surface border border-envicom-border rounded-md overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-envicom-muted">
              {query ? "No controllers match this search." : "No controllers in the registry yet."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-envicom-bg/50 text-envicom-muted text-[11px] uppercase tracking-wider font-mono">
                    <th className="text-left p-3">Serial #</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Model</th>
                    <th className="text-left p-3">Current FW</th>
                    <th className="text-left p-3">Last Update</th>
                    <th className="text-left p-3">Location</th>
                    <th className="text-left p-3">History</th>
                    {canWrite && <th className="p-3" />}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} data-testid={`controller-row-${c.id}`} className="border-t border-envicom-border hover:bg-envicom-surfaceHover/40">
                      <td className="p-3 font-mono text-envicom-text">
                        <div className="flex items-center gap-2"><Cpu className="w-3.5 h-3.5 text-envicom-muted" />#{c.serial_number}</div>
                      </td>
                      <td className="p-3 text-envicom-text">{c.customer || <span className="text-envicom-muted/60 italic">{LEVITO_PLACEHOLDER}</span>}</td>
                      <td className="p-3 font-mono text-envicom-muted">{c.chamber_model}</td>
                      <td className="p-3 font-mono">
                        {c.current_version ? (
                          <span className="px-2 py-0.5 rounded border border-envicom-border text-envicom-green">v{c.current_version}</span>
                        ) : (
                          <span className="text-envicom-muted">—</span>
                        )}
                      </td>
                      <td className="p-3 text-envicom-muted font-mono">{formatDate(c.last_update)}</td>
                      <td className="p-3 text-envicom-muted">{c.location || <span className="text-envicom-muted/60 italic">{LEVITO_PLACEHOLDER}</span>}</td>
                      <td className="p-3">
                        {c.update_history && c.update_history.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => setHistoryFor(c)}
                            data-testid={`controller-history-btn-${c.id}`}
                            className="inline-flex items-center gap-1 text-envicom-accent hover:underline text-xs font-mono"
                          >
                            <History className="w-3.5 h-3.5" /> {c.update_history.length}
                          </button>
                        ) : (
                          <span className="text-envicom-muted/60 text-xs">—</span>
                        )}
                      </td>
                      {canWrite && (
                        <td className="p-3 text-right space-x-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => {
                              setControllerDraft({
                                id: c.id,
                                serial_number: c.serial_number,
                                customer: c.customer || "",
                                chamber_model: c.chamber_model,
                                current_version: c.current_version || "",
                                last_update: c.last_update ? c.last_update.slice(0, 10) : "",
                                location: c.location || "",
                                notes: c.notes || "",
                              });
                              setShowControllerModal(true);
                            }}
                            data-testid={`controller-edit-${c.id}`}
                            className="text-envicom-muted hover:text-envicom-text text-xs font-mono"
                          >
                            Edit
                          </button>
                          <button type="button" onClick={() => deleteController(c.id)} className="text-envicom-muted hover:text-envicom-error" data-testid={`controller-delete-${c.id}`}>
                            <Trash2 className="w-4 h-4 inline" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {showControllerModal && (
        <Modal onClose={() => setShowControllerModal(false)} title={controllerDraft.id ? "Edit controller" : "Add controller"}>
          <form onSubmit={submitController} className="space-y-4" data-testid="controller-form">
            <Field label="Serial # (numeric, used by HMI for OTC)">
              <input
                type="number"
                min="1"
                required
                value={controllerDraft.serial_number}
                onChange={(e) => setControllerDraft({ ...controllerDraft, serial_number: e.target.value })}
                className={inputCls}
                data-testid="controller-serial-input"
                placeholder="e.g. 120454"
              />
            </Field>
            <Field label="Chamber model">
              <input
                required
                value={controllerDraft.chamber_model}
                onChange={(e) => setControllerDraft({ ...controllerDraft, chamber_model: e.target.value })}
                placeholder="e.g. WTC-200L"
                className={inputCls}
                data-testid="controller-model-input"
              />
            </Field>
            <Field label="Customer (optional)">
              <input
                value={controllerDraft.customer || ""}
                onChange={(e) => setControllerDraft({ ...controllerDraft, customer: e.target.value })}
                placeholder={LEVITO_PLACEHOLDER}
                className={inputCls}
                data-testid="controller-customer-input"
              />
            </Field>
            <Field label="Location (optional)">
              <input
                value={controllerDraft.location || ""}
                onChange={(e) => setControllerDraft({ ...controllerDraft, location: e.target.value })}
                placeholder={LEVITO_PLACEHOLDER}
                className={inputCls}
                data-testid="controller-location-input"
              />
            </Field>
            <Field label="Current firmware">
              <input
                value={controllerDraft.current_version || ""}
                onChange={(e) => setControllerDraft({ ...controllerDraft, current_version: e.target.value })}
                placeholder="2.4.1"
                className={inputCls}
                data-testid="controller-version-input"
              />
            </Field>
            <Field label="Last update">
              <input
                type="date"
                value={controllerDraft.last_update || ""}
                onChange={(e) => setControllerDraft({ ...controllerDraft, last_update: e.target.value })}
                className={inputCls}
                data-testid="controller-last-update-input"
              />
            </Field>
            <Field label="Notes">
              <textarea
                value={controllerDraft.notes || ""}
                onChange={(e) => setControllerDraft({ ...controllerDraft, notes: e.target.value })}
                rows={3}
                className={inputCls}
                data-testid="controller-notes-input"
              />
            </Field>
            {controllerDraft.id && (
              <p className="text-[11px] text-envicom-muted font-mono">
                Changes to <span className="text-envicom-text">Current firmware</span> and <span className="text-envicom-text">Last update</span> are recorded in the update history with your name.
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowControllerModal(false)} className="px-4 py-2 rounded border border-envicom-border text-envicom-muted text-sm font-mono">Cancel</button>
              <button type="submit" data-testid="controller-submit-btn" className="px-4 py-2 rounded bg-envicom-accent hover:bg-sky-400 text-black font-semibold font-mono text-sm">
                {controllerDraft.id ? "Save" : "Add"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {historyFor && (
        <Modal onClose={() => setHistoryFor(null)} title={`History · #${historyFor.serial_number}`}>
          <div data-testid="controller-history-modal" className="space-y-3">
            <div className="text-envicom-muted text-xs font-mono">
              {historyFor.chamber_model} · {historyFor.customer || LEVITO_PLACEHOLDER}
            </div>
            {historyFor.update_history.length === 0 ? (
              <div className="py-6 text-center text-envicom-muted text-sm">No updates recorded yet.</div>
            ) : (
              <ol className="relative border-l border-envicom-border ml-2 space-y-4">
                {historyFor.update_history.map((h, idx) => (
                  <li key={idx} className="pl-6 relative">
                    <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-envicom-accent border-2 border-envicom-bg" />
                    <div className="text-envicom-text text-sm font-mono">{formatDateTime(h.at)}</div>
                    <div className="text-envicom-muted text-[11px] font-mono">by {h.by_name} <span className="opacity-60">({h.by_email})</span></div>
                    <ul className="mt-2 space-y-1">
                      {Object.entries(h.changes || {}).map(([field, diff]) => {
                        const label = field === "current_version" ? "Firmware" : field === "last_update" ? "Last update" : field;
                        const from = field === "last_update" ? formatDate(diff.from) : (diff.from || "—");
                        const to = field === "last_update" ? formatDate(diff.to) : (diff.to || "—");
                        return (
                          <li key={field} className="flex items-center gap-2 text-xs font-mono bg-envicom-input border border-envicom-border rounded px-2 py-1.5">
                            <span className="text-envicom-muted uppercase tracking-wider text-[10px] w-20 shrink-0">{label}</span>
                            <span className="text-envicom-muted line-through">{from}</span>
                            <ArrowRight className="w-3 h-3 text-envicom-muted" />
                            <span className="text-envicom-green">{to}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

const inputCls = "w-full bg-envicom-input border border-envicom-border rounded px-3 py-2 text-envicom-text focus:outline-none focus:border-envicom-accent text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-envicom-muted text-xs uppercase tracking-wider font-mono">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-up" onClick={onClose}>
      <div className="bg-envicom-surface border border-envicom-border rounded-md w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-envicom-border">
          <h2 className="font-mono font-bold text-envicom-text">{title}</h2>
          <button onClick={onClose} className="text-envicom-muted hover:text-envicom-text" data-testid="modal-close-btn"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function blankController() {
  return { id: null, serial_number: "", customer: "", chamber_model: "", current_version: "", last_update: "", location: "", notes: "" };
}
